// src/pages/sessions/components/LiveSession/whiteboard/useWhiteboardSync.js
import { useCallback, useRef, useEffect } from 'react';
import { RoomEvent } from 'livekit-client';

const encoder = new TextEncoder();
const decoder = new TextDecoder();

/**
 * Hook for whiteboard data channel sync
 * Handles sending/receiving whiteboard messages via LiveKit data channels
 */
export function useWhiteboardSync({
  roomRef,
  roomConnected,
  localParticipantIdentity,
  isTeacherUser,
  currentVirtualRoomRef,
  teacherBroadcastRoomsRef,
  onMessage,
}) {
  const mountedRef = useRef(true);
  
  // Store callbacks in refs to avoid effect dependency churn
  const onMessageRef = useRef(onMessage);
  const localParticipantIdentityRef = useRef(localParticipantIdentity);
  const isTeacherUserRef = useRef(isTeacherUser);
  
  // Keep refs updated
  useEffect(() => {
    onMessageRef.current = onMessage;
  }, [onMessage]);
  
  useEffect(() => {
    localParticipantIdentityRef.current = localParticipantIdentity;
  }, [localParticipantIdentity]);
  
  useEffect(() => {
    isTeacherUserRef.current = isTeacherUser;
  }, [isTeacherUser]);
  
  // Max safe payload size (LiveKit limit is 65535, leave room for overhead)
  const MAX_CHUNK_SIZE = 50000;
  
  // Broadcast a whiteboard message to all participants
  // Automatically chunks messages that exceed LiveKit's 65KB data channel limit.
  const broadcastMessage = useCallback((message) => {
    if (!roomRef.current?.localParticipant) {
      console.warn('[Whiteboard] Cannot broadcast - no local participant');
      return;
    }
    
    const jsonStr = JSON.stringify(message);
    const data = encoder.encode(jsonStr);
    
    if (data.byteLength <= MAX_CHUNK_SIZE) {
      // Small enough — send directly
      roomRef.current.localParticipant.publishData(data, { reliable: true })
        .catch(err => {
          console.error('[Whiteboard] Failed to broadcast:', err);
        });
      return;
    }
    
    // Too large — chunk the JSON string
    const syncId = `sync-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    const totalChunks = Math.ceil(jsonStr.length / MAX_CHUNK_SIZE);
    console.log(`[Whiteboard] Message too large (${data.byteLength} bytes), splitting into ${totalChunks} chunks`);
    
    const sendChunk = async (index) => {
      const start = index * MAX_CHUNK_SIZE;
      const chunk = jsonStr.slice(start, start + MAX_CHUNK_SIZE);
      const chunkMsg = {
        type: 'whiteboard_sync_chunk',
        syncId,
        chunkIndex: index,
        totalChunks,
        data: chunk,
      };
      const chunkData = encoder.encode(JSON.stringify(chunkMsg));
      await roomRef.current.localParticipant.publishData(chunkData, { reliable: true });
    };
    
    // Send chunks sequentially (reliable channel preserves order)
    (async () => {
      try {
        for (let i = 0; i < totalChunks; i++) {
          await sendChunk(i);
        }
        console.log(`[Whiteboard] All ${totalChunks} chunks sent`);
      } catch (err) {
        console.error('[Whiteboard] Failed to send chunk:', err);
      }
    })();
  }, [roomRef]);
  
  // Chunk reassembly buffer: syncId → { chunks: Map<index, string>, totalChunks }
  const chunkBufferRef = useRef(new Map());

  // Set up data channel listener - ONLY depends on roomConnected
  useEffect(() => {
    if (!roomConnected || !roomRef.current) {
      return;
    }
    
    const room = roomRef.current;
    
    const handleDataReceived = (payload, participant) => {
      if (!mountedRef.current) return;
      
      try {
        const message = JSON.parse(decoder.decode(payload));
        
        // Only process whiteboard messages
        if (!message.type?.startsWith('wb_') && !message.type?.startsWith('whiteboard_')) {
          return;
        }
        
        // Handle chunked sync messages — reassemble before processing
        if (message.type === 'whiteboard_sync_chunk') {
          const { syncId, chunkIndex, totalChunks, data: chunkData } = message;
          
          if (!chunkBufferRef.current.has(syncId)) {
            chunkBufferRef.current.set(syncId, { chunks: new Map(), totalChunks });
          }
          
          const buffer = chunkBufferRef.current.get(syncId);
          buffer.chunks.set(chunkIndex, chunkData);
          
          console.log(`[WhiteboardSync] Chunk ${chunkIndex + 1}/${totalChunks} received for ${syncId}`);
          
          if (buffer.chunks.size === totalChunks) {
            // All chunks received — reassemble
            let fullJson = '';
            for (let i = 0; i < totalChunks; i++) {
              fullJson += buffer.chunks.get(i);
            }
            chunkBufferRef.current.delete(syncId);
            
            // Clean up stale buffers (older than 30s)
            const now = Date.now();
            for (const [id] of chunkBufferRef.current) {
              const ts = parseInt(id.split('-')[1]) || 0;
              if (now - ts > 30000) chunkBufferRef.current.delete(id);
            }
            
            try {
              const reassembled = JSON.parse(fullJson);
              console.log(`[WhiteboardSync] Reassembled chunked message: ${reassembled.type}`);
              if (onMessageRef.current) {
                onMessageRef.current(reassembled, participant);
              }
            } catch (e) {
              console.error('[WhiteboardSync] Failed to parse reassembled message:', e);
            }
          }
          return;
        }
        
        
        // Self-echo guard using ref for current identity
        if (message.authorIdentity && message.authorIdentity === localParticipantIdentityRef.current) {
          return;
        }
        if (message.type === 'whiteboard_sync_request' && message.requesterIdentity === localParticipantIdentityRef.current) {
          return;
        }
        
        // Note: Broadcast scope filtering is handled at a higher level:
        // - shouldShowWhiteboard gates whether the canvas component renders
        // - useSubscriptions gates whether the student receives teacher data
        // No need to filter here — if we receive a message, we should process it.
        
        console.log('[WhiteboardSync] Forwarding message:', message.type);
        
        // Forward to handler using ref
        if (onMessageRef.current) {
          onMessageRef.current(message, participant);
        }
      } catch (e) {
        // Not a JSON message or not for whiteboard, ignore
      }
    };
    
    room.on(RoomEvent.DataReceived, handleDataReceived);
    
    return () => {
      room.off(RoomEvent.DataReceived, handleDataReceived);
    };
  }, [roomConnected, roomRef, currentVirtualRoomRef, teacherBroadcastRoomsRef]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    broadcastMessage,
  };
}
